import type {
  CustomerTeamMember,
  CustomerTeamMemberWithLabels,
} from '@clockwise/clients/customer-team';
import { ApiError } from '@clockwise/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { RowActionSpacer, RowActions } from '@/components/custom/row-actions';
import { SearchInput } from '@/components/custom/search-input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { usePageWindow } from '@/hooks/use-page-window';
import { customerTeamApi } from '@/lib/api';
import { isForbidden } from '@/lib/errors';
import { useHasPermission } from '@/lib/permissions';
import { queryKeys } from '@/lib/query-keys';
import { localTodayIso, localTomorrowIso } from '@/lib/utils';
import { AsOfFilterToggle } from './as-of-filter-toggle';
import { ConfirmDialog } from './confirm-dialog';
import { CustomerTeamMemberFormSheet } from './customer-team-member-form-sheet';
import { CustomerTeamMemberViewSheet } from './customer-team-member-view-sheet';
import { PaginationFooter } from './pagination-footer';
import { RequirePermission } from './require-permission';
import { SHEET_SWAP_DELAY_MS } from './sheet-swap';

/** Server page size for the team list. Large internal-customer teams run to
 *  hundreds of members, so the tab pages rather than fetching all at once. */
const TEAM_PAGE_SIZE = 25;

/**
 * Customer-detail Team tab. Shows memberships in effect on the chosen
 * date (defaults to today). Toolbar:
 *   - As-of date picker (jumps the view to any historical snapshot)
 *   - "Show all (incl. ended)" toggle — flips the list endpoint
 *   - "Add member" gated on customer.team.edit
 *
 * Rows: user name, service role, lead badge, effective range, edit + end /
 * restore actions.
 */
interface Props {
  customerId: string;
}

// One modal sheet at a time: add/edit drive the form sheet, view drives the
// read-only sheet. A single union (rather than parallel open/target booleans)
// keeps "which sheet, which row" unambiguous and means only one Radix modal is
// ever open.
type SheetState =
  | { mode: 'add' }
  | { mode: 'edit'; member: CustomerTeamMember }
  | { mode: 'view'; member: CustomerTeamMemberWithLabels }
  | null;

export function CustomerTeamPanel({ customerId }: Props) {
  const queryClient = useQueryClient();
  const canView = useHasPermission('customer.team.view');
  const canEdit = useHasPermission('customer.team.edit');

  const [asOf, setAsOf] = useState<string>('');
  const [showAll, setShowAll] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [sheet, setSheet] = useState<SheetState>(null);
  const [confirmEnd, setConfirmEnd] = useState<CustomerTeamMemberWithLabels | null>(null);

  // Debounce the search box so we issue one request after typing settles,
  // not one per keystroke.
  const debouncedSearch = useDebouncedValue(search.trim(), 300);

  // Any filter change resets to the first page (a search on page 4 should
  // land you on page 1 of the new result set).
  // value we reset; including it would defeat the purpose.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, asOf, showAll]);

  const q = debouncedSearch || undefined;
  const offset = page * TEAM_PAGE_SIZE;

  const inEffectQuery = useQuery({
    queryKey: queryKeys.customerTeam.inEffect(customerId, {
      asOf: asOf || undefined,
      q,
      limit: TEAM_PAGE_SIZE,
      offset,
    }),
    queryFn: () =>
      customerTeamApi.listInEffect(customerId, {
        asOf: asOf || undefined,
        q,
        limit: TEAM_PAGE_SIZE,
        offset,
      }),
    enabled: !showAll,
    // Keep the current page visible while the next page/search loads.
    placeholderData: keepPreviousData,
  });
  const allQuery = useQuery({
    queryKey: queryKeys.customerTeam.listAll(customerId, { q, limit: TEAM_PAGE_SIZE, offset }),
    queryFn: () => customerTeamApi.listAll(customerId, { q, limit: TEAM_PAGE_SIZE, offset }),
    enabled: showAll,
    placeholderData: keepPreviousData,
  });
  const active = showAll ? allQuery : inEffectQuery;
  const members = active.data?.data ?? [];
  const total = active.data?.meta?.total ?? 0;
  const { pageCount, shownOffset } = usePageWindow({
    page,
    setPage,
    pageSize: TEAM_PAGE_SIZE,
    total,
    responseOffset: active.data?.meta?.offset,
  });

  // Member + service-role names are resolved server-side and arrive on each
  // row (`userName` / `serviceRoleName`). This used to be a client-side
  // join against a `limit: 200` tenant-wide users list, which silently
  // rendered "…" for any member whose user sorted past row 200. A `null`
  // label now means the referenced user/role was deleted — rendered as
  // "Unknown", not a loading placeholder.

  const endMutation = useMutation({
    // `effectiveTo` is an EXCLUSIVE end boundary (active while effectiveTo >
    // date), so ending "today" means the member stays active through the end
    // of today and loses access tomorrow — hence local tomorrow, not today.
    // Sending today would revoke access for the entire current day. Computed
    // client-side so the server (UTC) doesn't have to guess the caller's local
    // date.
    mutationFn: (id: string) => customerTeamApi.end(id, { effectiveTo: localTomorrowIso() }),
    onSuccess: () => {
      toast.success('Membership ended');
      queryClient.invalidateQueries({ queryKey: queryKeys.customerTeam.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      setConfirmEnd(null);
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to end membership'),
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <SearchInput
          value={search}
          onValueChange={setSearch}
          placeholder="Search name or email"
          aria-label="Search team by name or email"
          className="w-56"
          inputClassName="h-8"
        />
        <AsOfFilterToggle
          asOf={asOf}
          onAsOfChange={setAsOf}
          showAll={showAll}
          onShowAllChange={setShowAll}
          showAllLabel="Show all (incl. ended)"
          // Spell out the current scope so the default view doesn't read as
          // "the whole team" — by default only memberships in effect on the
          // as-of date (today) are listed.
          statusText={
            showAll
              ? 'Showing all members, including ended.'
              : asOf
                ? `Showing members active as of ${asOf}.`
                : 'Showing members active as of today.'
          }
        />
        <div className="ml-auto">
          <RequirePermission permission="customer.team.edit">
            <Button size="sm" onClick={() => setSheet({ mode: 'add' })}>
              <Plus />
              Add member
            </Button>
          </RequirePermission>
        </div>
      </div>

      <div className="rounded-md border">
        {active.isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : active.isError ? (
          isForbidden(active.error) ? (
            <p className="p-6 text-center text-sm text-muted-foreground">
              You don’t have access to this customer’s team.
            </p>
          ) : (
            <p className="p-4 text-sm text-destructive">
              {active.error instanceof ApiError ? active.error.message : 'Failed to load team.'}
            </p>
          )
        ) : members.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground">
            {debouncedSearch
              ? `No members match “${debouncedSearch}”.`
              : showAll
                ? 'No team members on record.'
                : 'No active team members on this date.'}
          </p>
        ) : (
          <Table>
            {/* `bg-surface-subtle` — the same cream header tint every other
                list table uses (see `sticky-table-scroll.tsx`); this table
                isn't wrapped in that component (it scrolls with the page,
                not internally), so the tint has to be stated here instead. */}
            <TableHeader className="bg-surface-subtle">
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Service role</TableHead>
                <TableHead>Effective</TableHead>
                <TableHead className="w-24 text-right" aria-label="actions" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((m) => {
                const ended = m.effectiveTo != null && m.effectiveTo <= (asOf || localTodayIso());
                return (
                  <TableRow
                    key={m.id}
                    className={canView ? 'cursor-pointer' : undefined}
                    onClick={canView ? () => setSheet({ mode: 'view', member: m }) : undefined}
                  >
                    <TableCell>
                      {m.userName != null ? (
                        // The row itself opens the read-only view sheet, so the
                        // link has to swallow its own click or you'd navigate
                        // away AND leave a sheet open behind you.
                        <Link
                          to="/users/$id"
                          params={{ id: m.userId }}
                          className="font-medium text-primary underline-offset-2 hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {m.userName}
                        </Link>
                      ) : (
                        <span className="font-medium">Unknown user</span>
                      )}
                      {m.teamLead ? (
                        <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-900 dark:bg-amber-900/30 dark:text-amber-200">
                          Lead
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-sm">{m.serviceRoleName ?? '—'}</TableCell>
                    <TableCell className="text-xs">
                      {m.effectiveFrom} →{' '}
                      <span className={ended ? 'text-muted-foreground' : 'font-medium'}>
                        {m.effectiveTo ?? 'Current'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {canEdit ? (
                        <RowActions>
                          <Button
                            size="icon-sm"
                            variant="ghost"
                            aria-label="Edit"
                            onClick={(e) => {
                              // Don't let the click bubble to the row, which
                              // would open the read-only view sheet.
                              e.stopPropagation();
                              setSheet({ mode: 'edit', member: m });
                            }}
                          >
                            <Pencil />
                          </Button>
                          {m.effectiveTo == null ? (
                            <Button
                              size="icon-sm"
                              variant="ghost"
                              aria-label="End membership"
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfirmEnd(m);
                              }}
                              disabled={endMutation.isPending}
                            >
                              <Trash2 />
                            </Button>
                          ) : (
                            // Keeps the pencil in the same lane as the rows
                            // that still have an end-membership button.
                            <RowActionSpacer />
                          )}
                        </RowActions>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <PaginationFooter
        shownOffset={shownOffset}
        shownCount={members.length}
        total={total}
        pageSize={TEAM_PAGE_SIZE}
        page={page}
        pageCount={pageCount}
        isFetching={active.isFetching}
        onPrevious={() => setPage((p) => Math.max(0, p - 1))}
        onNext={() => setPage((p) => p + 1)}
      />

      <CustomerTeamMemberFormSheet
        open={sheet?.mode === 'add' || sheet?.mode === 'edit'}
        onOpenChange={(o) => {
          if (!o) setSheet(null);
        }}
        customerId={customerId}
        member={sheet?.mode === 'edit' ? sheet.member : null}
      />

      <CustomerTeamMemberViewSheet
        open={sheet?.mode === 'view'}
        onOpenChange={(o) => {
          if (!o) setSheet(null);
        }}
        member={sheet?.mode === 'view' ? sheet.member : null}
        onEdit={() => {
          if (sheet?.mode !== 'view') return;
          // Close the view sheet, then open the edit form only after its modal
          // overlay tears down — two Radix modal dialogs transitioning in the
          // same tick can leave the page inert (body pointer-events: none).
          const { member } = sheet;
          setSheet(null);
          window.setTimeout(() => setSheet({ mode: 'edit', member }), SHEET_SWAP_DELAY_MS);
        }}
      />

      <ConfirmDialog
        open={confirmEnd != null}
        onCancel={() => setConfirmEnd(null)}
        title="End this membership?"
        description={`${confirmEnd?.userName ?? 'This user'}'s membership will end today. They keep access through the end of today and lose it from tomorrow.`}
        confirmLabel={endMutation.isPending ? 'Ending…' : 'End'}
        confirmDisabled={endMutation.isPending}
        destructive
        onConfirm={() => {
          if (confirmEnd && !endMutation.isPending) endMutation.mutate(confirmEnd.id);
        }}
      />
    </div>
  );
}
